import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, FileUp, Globe, Inbox, Layers, MessageCircle, Play, ArrowDownWideNarrow, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const CHANNELS = [
  { key: "web", label: "Web Applications", icon: Globe, hint: "Applied directly on the website, or added manually by staff." },
  { key: "whatsapp", label: "WhatsApp Leads", icon: MessageCircle, hint: "Arrived via the WhatsApp (Pabbly) automation." },
  { key: "csv", label: "Uploaded Data", icon: FileUp, hint: "Imported in bulk via CSV upload." },
];

export default function UnassignedPool() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRecruiter = user.role === "recruiter";
  const basePath = isRecruiter ? "/recruiter/applications" : "/admin/applications";

  const [channel, setChannel] = useState("web");
  const [sort, setSort] = useState("oldest");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({ web: 0, whatsapp: 0, csv: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [conflict, setConflict] = useState(null); // M17 one-undisposed blocking info

  const loadCounts = useCallback(() => {
    api.get("/applications/unassigned/channel-counts").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/applications/unassigned", { params: { channel, sort, q: q || undefined } })
      .then((res) => setApps(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [channel, sort, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Debounced so typing a name doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const startWorking = async (app) => {
    setClaiming(app.id);
    try {
      const res = await api.post(`/applications/${app.id}/claim`);
      const together = res.data?.claimed_together || 0;
      const who = app.member?.name || app.candidate?.full_name;
      toast.success(together > 0
        ? `You are now working on ${who} — ${together} other application${together > 1 ? "s" : ""} from this candidate claimed together`
        : `You are now working on ${who}'s application`);
      navigate(`/recruiter/applications/${app.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "ACTIVE_CANDIDATE_EXISTS") {
        setConflict(detail);
      } else {
        toast.error(getErrorMessage(err));
      }
      load();
      loadCounts();
    } finally {
      setClaiming(null);
    }
  };

  const active = CHANNELS.find((c) => c.key === channel);
  const ActiveIcon = active.icon;

  return (
    <div className="space-y-6" data-testid="fresh-leads-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Fresh Leads</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isRecruiter
            ? "Unassigned leads waiting to be worked — click “Start Working” to become the owner. First come, first served."
            : "Unassigned leads across the whole team, grouped by how they arrived. Numbers are live for every recruiter."}
        </p>
      </div>

      {/* Search by name/mobile/application code — finds a specific lead across
          ALL channels, not just whichever tab happens to be selected. */}
      <div className="relative">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={qInput} onChange={(e) => setQInput(e.target.value)}
          placeholder="Search all Fresh Leads by name, mobile, or application ID..."
          className="pl-9 pr-9 rounded-lg bg-white" data-testid="fresh-leads-search" />
        {qInput && (
          <button type="button" onClick={() => setQInput("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="fresh-leads-search-clear">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!q && (
        <>
          <Tabs value={channel} onValueChange={setChannel}>
            <TabsList className="h-auto flex-wrap gap-1 bg-muted/60 p-1" data-testid="fresh-leads-tabs">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                return (
                  <TabsTrigger key={c.key} value={c.key} data-testid={`fresh-leads-tab-${c.key}`}
                    className="gap-2 data-[state=active]:bg-white">
                    <Icon className="h-4 w-4" />
                    <span>{c.label}</span>
                    <Badge variant="secondary"
                      data-testid={`fresh-leads-tabcount-${c.key}`}
                      className={`ml-1 rounded-full px-2 ${counts[c.key] > 0 ? "bg-primary text-primary-foreground" : "bg-muted-foreground/15 text-muted-foreground"}`}>
                      {counts[c.key] ?? 0}
                    </Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          {/* Prominent "Not Contacted" count for the active channel */}
          <Card className="rounded-xl border-border shadow-none bg-gradient-to-br from-primary/5 to-transparent" data-testid="not-contacted-banner">
            <CardContent className="p-5 flex items-center gap-4">
              <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <ActiveIcon className="h-6 w-6" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Not Contacted · {active.label}</p>
                <p className="text-3xl font-heading font-bold tabular-nums" data-testid="not-contacted-count">
                  {counts[channel] ?? 0}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{active.hint}</p>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Sort control — oldest-first by default so the longest-waiting leads surface first */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {apps.length} {q ? "result" : "lead"}{apps.length === 1 ? "" : "s"} {q ? `for "${q}"` : "in this channel"}
        </p>
        <div className="inline-flex items-center rounded-lg border border-border bg-white p-0.5" data-testid="fresh-leads-sort">
          <ArrowDownWideNarrow className="h-3.5 w-3.5 text-muted-foreground ml-1.5 mr-1" />
          {[
            { key: "oldest", label: "Oldest first" },
            { key: "newest", label: "Newest first" },
          ].map((o) => (
            <button
              key={o.key}
              onClick={() => setSort(o.key)}
              data-testid={`fresh-leads-sort-${o.key}`}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                sort === o.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      ) : apps.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={Inbox} title={`No ${active.label.toLowerCase()} waiting`}
            description="New leads in this channel will appear here for any recruiter to pick up."
            testId="unassigned-empty" />
        </Card>
      ) : (
        <div className="space-y-3" data-testid="unassigned-list">
          {apps.map((app) => {
            const dupCount = app.pending_siblings_count || 0;
            const who = app.member?.name || app.candidate?.full_name || "Candidate";
            const mob = app.member?.mobile || app.candidate?.mobile || "";
            return (
            <Card key={app.id}
                  className={`rounded-xl border-border shadow-none hover:border-primary/40 transition-colors ${dupCount > 0 ? "border-l-4 border-l-amber-400" : ""}`}
                  data-testid="unassigned-application-card">
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm shrink-0">
                    {who.charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium leading-tight flex items-center gap-2 flex-wrap">
                      {who} <span className="font-mono text-xs text-muted-foreground">{mob}</span>
                      {dupCount > 0 && (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline"
                                     className="rounded-full gap-1 border-amber-300 bg-amber-50 text-amber-700 text-[11px] font-medium cursor-default"
                                     data-testid={`duplicate-candidate-badge-${app.id}`}>
                                <Layers className="h-3 w-3" />
                                {dupCount} other pending {dupCount > 1 ? "applications" : "application"}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="font-medium mb-1">Same candidate — {dupCount + 1} pending applications</p>
                              <p className="text-xs">Starting work claims all of them together:</p>
                              <ul className="text-xs mt-1 list-disc pl-4">
                                {(app.sibling_jobs || []).slice(0, 5).map((j, i) => <li key={i}>{j}</li>)}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                      <Building2 className="h-3 w-3" />{app.job?.hiring_brand || "—"} · {app.job?.title || "General"} · Applied {formatDate(app.created_at)} · <span className="font-mono">{app.code}</span> · Source: {app.source}
                    </p>
                  </div>
                  {isRecruiter ? (
                    <Button onClick={() => startWorking(app)} disabled={claiming === app.id} className="rounded-lg shrink-0" data-testid={`start-working-${app.id}`}>
                      <Play className="h-4 w-4 mr-2" /> Start Working
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => navigate(`${basePath}/${app.id}`)} className="rounded-lg shrink-0 bg-white" data-testid={`view-lead-${app.id}`}>
                      View
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* M17: one-undisposed-candidate blocking dialog */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Finish your current candidate first</DialogTitle>
            <DialogDescription data-testid="conflict-message">{conflict?.message}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="rounded-lg flex-1" data-testid="conflict-goto-btn"
                    onClick={() => { const id = conflict?.active_application_id; setConflict(null); navigate(`/recruiter/applications/${id}`); }}>
              Open {conflict?.candidate_name} ({conflict?.active_app_code})
            </Button>
            <Button variant="outline" className="rounded-lg bg-white" data-testid="conflict-close-btn" onClick={() => setConflict(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
